import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  Clock3,
  Sparkles,
  Code2,
  BrainCircuit,
  ShieldCheck,
  Database,
  Terminal,
  BarChart3,
  CheckCircle2,
} from "lucide-react";

const courses = [
  {
    title: "Software Engineering",
    short: "BUILD",
    description:
      "Design, build, test and deploy modern software applications. Learn the engineering mindset and practical skills required to build products used by real people.",
    duration: "6 – 9 Months",
    level: "Beginner Friendly",
    outcome: "Full-Stack Engineer",
    color: "#59B5CD",
    icon: Code2,
    modules: [
      "Frontend Development",
      "Backend Engineering",
      "Databases & APIs",
      "Cloud & Deployment",
    ],
    link: "/software-engineering",
  },
  {
    title: "Artificial Intelligence",
    short: "INTELLIGENCE",
    description:
      "Explore machine learning, neural networks and intelligent systems while learning how modern AI products are designed, trained and deployed.",
    duration: "6 Months",
    level: "Beginner Friendly",
    outcome: "AI / ML Engineer",
    color: "#ECB32C",
    icon: BrainCircuit,
    modules: [
      "Python for AI",
      "Machine Learning",
      "Deep Learning",
      "Generative AI",
    ],
    link: "/ai",
  },
  {
    title: "Cyber Security",
    short: "DEFEND",
    description:
      "Learn how to identify vulnerabilities, defend digital infrastructure and understand the techniques used by modern security professionals.",
    duration: "3 Months",
    level: "Beginner Friendly",
    outcome: "Cybersecurity Professional",
    color: "#CF385D",
    icon: ShieldCheck,
    modules: [
      "Network Security",
      "Ethical Hacking",
      "Threat Intelligence",
      "Security Operations",
    ],
    link: "/cyber-security",
  },
  {
    title: "Data Engineering",
    short: "ENGINEER DATA",
    description:
      "Build the infrastructure that moves, transforms and prepares data for analytics, business intelligence and artificial intelligence.",
    duration: "4 Months",
    level: "Beginner Friendly",
    outcome: "Data Engineer",
    color: "#59B5CD",
    icon: Database,
    modules: [
      "SQL & Databases",
      "ETL Pipelines",
      "Cloud Data",
      "Big Data",
    ],
    link: "/data-engineering",
  },
  {
    title: "Certified Ethical Hacker",
    short: "HACK ETHICALLY",
    description:
      "Understand how attackers think, discover vulnerabilities and strengthen systems using ethical hacking methodologies.",
    duration: "3.5 Months",
    level: "Beginner Friendly",
    outcome: "Ethical Hacker",
    color: "#ECB32C",
    icon: Terminal,
    modules: [
      "Reconnaissance",
      "Vulnerability Assessment",
      "Penetration Testing",
      "CEH Preparation",
    ],
    link: "/ceh",
  },
  {
    title: "Data Analysis",
    short: "DISCOVER",
    description:
      "Turn raw information into meaningful business insights using Excel, SQL, Python and powerful data visualization tools.",
    duration: "2 Months",
    level: "Beginner Friendly",
    outcome: "Data Analyst",
    color: "#CF385D",
    icon: BarChart3,
    modules: [
      "Excel & Spreadsheets",
      "SQL",
      "Python",
      "Data Visualization",
    ],
    link: "/data-analysis",
  },
];

const ExploreOur = () => {
  const [activeCourse, setActiveCourse] = useState(0);

  const course = courses[activeCourse];
  const Icon = course.icon;

  return (
    <section className="relative overflow-hidden bg-[#08090B] py-28 text-white md:py-40">

      {/* =====================================================
          BACKGROUND
      ====================================================== */}

      <div className="pointer-events-none absolute inset-0">

        <div
          className="absolute left-[-200px] top-[-150px] h-[600px] w-[600px] rounded-full blur-[160px]"
          style={{
            backgroundColor: `${course.color}18`,
            transition: "background-color 0.6s ease",
          }}
        />

        <div className="absolute bottom-[-250px] right-[-150px] h-[600px] w-[600px] rounded-full bg-[#59B5CD]/10 blur-[160px]" />

        {/* Grid */}
        <div
          className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.8) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

      </div>


      <div className="relative mx-auto max-w-[1450px] px-6 sm:px-8 md:px-12 lg:px-20">

        {/* =====================================================
            HEADER
        ====================================================== */}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl"
        >

          <div className="mb-7 flex items-center gap-3">

            <span className="h-2 w-2 rounded-full bg-[#59B5CD]" />

            <span className="text-[10px] uppercase tracking-[0.4em] text-white/40">
              Explore the Academy
            </span>

          </div>

          <h2 className="font-[ubuntu] text-5xl font-semibold leading-[0.88] tracking-[-0.06em] sm:text-6xl md:text-8xl">

            Choose your
            <br />

            <span className="text-white/20">
              next advantage.
            </span>

          </h2>

          <p className="mt-8 max-w-2xl text-sm leading-7 text-white/45 md:text-base md:leading-8">
            Practical technology education designed around the skills,
            projects and mindset required to thrive in the global digital
            economy.
          </p>

        </motion.div>


        {/* =====================================================
            COURSE SELECTOR
        ====================================================== */}

        <div className="mt-20 grid gap-4 border-y border-white/10 py-4 lg:grid-cols-6">

          {courses.map((item, index) => {

            const ItemIcon = item.icon;
            const active = activeCourse === index;

            return (
              <button
                key={item.title}
                onClick={() => setActiveCourse(index)}
                className={`group relative flex items-center gap-4 p-5 text-left transition-all duration-300 ${
                  active
                    ? "bg-white/[0.07]"
                    : "hover:bg-white/[0.04]"
                }`}
              >

                {/* Active line */}

                <span
                  className={`absolute bottom-0 left-0 h-[2px] transition-all duration-500 ${
                    active ? "w-full" : "w-0"
                  }`}
                  style={{ backgroundColor: item.color }}
                />

                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition"
                  style={{
                    borderColor: active
                      ? item.color
                      : "rgba(255,255,255,.1)",
                    color: active ? item.color : "rgba(255,255,255,.4)",
                  }}
                >
                  <ItemIcon className="h-4 w-4" />
                </div>

                <div>

                  <span className="block text-[8px] uppercase tracking-[0.25em] text-white/25">
                    0{index + 1}
                  </span>

                  <span
                    className={`mt-1 block text-xs font-medium leading-4 transition ${
                      active ? "text-white" : "text-white/45"
                    }`}
                  >
                    {item.title}
                  </span>

                </div>

              </button>
            );
          })}

        </div>


        {/* =====================================================
            MAIN COURSE EXPERIENCE
        ====================================================== */}

        <div className="mt-12 grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">

          {/* LEFT — COURSE INFORMATION */}

          <AnimatePresence mode="wait">

            <motion.div
              key={course.title}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 30 }}
              transition={{ duration: 0.45 }}
              className="relative min-h-[570px] overflow-hidden border border-white/10 bg-white/[0.035] p-8 sm:p-10 md:p-14"
            >

              {/* Large background number */}

              <div
                className="pointer-events-none absolute right-[-20px] top-[-40px] font-[ubuntu] text-[220px] font-bold leading-none tracking-[-0.1em]"
                style={{ color: `${course.color}08` }}
              >
                0{activeCourse + 1}
              </div>


              <div className="relative">

                <div className="flex items-center justify-between">

                  <span
                    className="text-[9px] font-medium uppercase tracking-[0.35em]"
                    style={{ color: course.color }}
                  >
                    {course.short}
                  </span>

                  <Sparkles
                    className="h-5 w-5"
                    style={{ color: course.color }}
                  />

                </div>


                <h3 className="mt-12 max-w-3xl font-[ubuntu] text-4xl font-semibold leading-[0.92] tracking-[-0.055em] sm:text-5xl md:text-7xl">
                  {course.title}
                </h3>


                <p className="mt-8 max-w-2xl text-sm leading-7 text-white/45 md:text-base md:leading-8">
                  {course.description}
                </p>


                {/* META */}

                <div className="mt-10 flex flex-wrap gap-3">

                  <div className="flex items-center gap-2 border border-white/10 bg-white/[0.04] px-4 py-3">
                    <Clock3 className="h-4 w-4 text-white/30" />

                    <div>
                      <span className="block text-[8px] uppercase tracking-[0.2em] text-white/25">
                        Duration
                      </span>

                      <span className="text-xs text-white/80">
                        {course.duration}
                      </span>
                    </div>

                  </div>


                  <div className="border border-white/10 bg-white/[0.04] px-4 py-3">

                    <span className="block text-[8px] uppercase tracking-[0.2em] text-white/25">
                      Level
                    </span>

                    <span className="text-xs text-white/80">
                      {course.level}
                    </span>

                  </div>


                  <div
                    className="border px-4 py-3"
                    style={{
                      borderColor: `${course.color}40`,
                      backgroundColor: `${course.color}10`,
                    }}
                  >

                    <span className="block text-[8px] uppercase tracking-[0.2em] text-white/30">
                      Career Path
                    </span>

                    <span
                      className="text-xs"
                      style={{ color: course.color }}
                    >
                      {course.outcome}
                    </span>

                  </div>

                </div>


                {/* CTA */}

                <a
                  href={course.link}
                  className="group mt-12 inline-flex items-center gap-4 border border-white/15 px-7 py-4 text-[10px] font-medium uppercase tracking-[0.25em] transition duration-300 hover:border-white hover:bg-white hover:text-black"
                >

                  Explore Program

                  <ArrowUpRight className="h-4 w-4 transition duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />

                </a>

              </div>

            </motion.div>

          </AnimatePresence>


          {/* RIGHT — WHAT YOU WILL LEARN */}

          <AnimatePresence mode="wait">

            <motion.div
              key={`modules-${course.title}`}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.45 }}
              className="relative overflow-hidden border border-white/10 bg-white/[0.02] p-8 sm:p-10 md:p-12"
            >

              <div className="flex items-center justify-between border-b border-white/10 pb-6">

                <div>

                  <span className="text-[9px] uppercase tracking-[0.35em] text-white/25">
                    Program curriculum
                  </span>

                  <h4 className="mt-3 font-[ubuntu] text-2xl font-semibold tracking-[-0.04em]">
                    What you'll learn
                  </h4>

                </div>

                <div
                  className="flex h-12 w-12 items-center justify-center rounded-full"
                  style={{
                    backgroundColor: `${course.color}15`,
                    color: course.color,
                  }}
                >
                  <Icon className="h-5 w-5" />
                </div>

              </div>


              <div className="mt-8 space-y-3">

                {course.modules.map((module, index) => (

                  <motion.div
                    key={module}
                    initial={{ opacity: 0, x: 15 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      delay: index * 0.08,
                      duration: 0.35,
                    }}
                    className="group flex items-center gap-4 border border-white/10 p-5 transition hover:border-white/25 hover:bg-white/[0.04]"
                  >

                    <CheckCircle2
                      className="h-4 w-4 shrink-0"
                      style={{ color: course.color }}
                    />

                    <span className="text-sm text-white/60 transition group-hover:text-white">
                      {module}
                    </span>

                    <span className="ml-auto text-[9px] text-white/15">
                      0{index + 1}
                    </span>

                  </motion.div>

                ))}

              </div>


              {/* Bottom message */}

              <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-black/20 p-6">

                <p className="text-xs leading-5 text-white/30">
                  Learn by building. Practice with real-world projects.
                  Develop skills that go beyond the classroom.
                </p>

              </div>

            </motion.div>

          </AnimatePresence>

        </div>


        {/* =====================================================
            BOTTOM STATEMENT
        ====================================================== */}

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="mt-24 flex flex-col justify-between gap-8 border-t border-white/10 pt-8 md:flex-row md:items-center"
        >

          <p className="max-w-xl text-xs leading-6 text-white/30">
            From your first line of code to your first professional
            opportunity, Zulfah Academy is built to help you move forward.
          </p>

          <div className="flex items-center gap-3">

            <span className="h-2 w-2 animate-pulse rounded-full bg-[#ECB32C]" />

            <span className="text-[9px] uppercase tracking-[0.3em] text-white/35">
              Applications open
            </span>

          </div>

        </motion.div>

      </div>

    </section>
  );
};

export default ExploreOur;